import React, { useState, useEffect } from 'react';
import { etapasAPI, marcasEtapaAPI, tiposProjetoAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Tag, Save, Hash, Type, Calendar, Database } from 'lucide-react';

const TIPO_ICONS = {
  texto: Type,
  numero: Hash,
  data: Calendar,
};

const TIPO_LABELS = {
  texto: 'Texto',
  numero: 'Número',
  data: 'Data',
};

const LEGACY_FIELDS = [
  { value: 'numero_proposta', label: 'Nº da Proposta (legado)' },
  { value: 'numero_contrato', label: 'Número do Contrato (legado)' },
  { value: 'valor_servico', label: 'Valor do Serviço (legado)' },
  { value: 'agencia', label: 'Agência (legado)' },
  { value: 'conta', label: 'Conta (legado)' },
  { value: 'banco', label: 'Banco (legado)' },
];

const Marcas = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [marcas, setMarcas] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [tiposProjeto, setTiposProjeto] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [migrateDialog, setMigrateDialog] = useState(false);
  const [migrateMarca, setMigrateMarca] = useState(null);
  const [migrateField, setMigrateField] = useState('numero_proposta');
  const [migrateOverwrite, setMigrateOverwrite] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrateResult, setMigrateResult] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    campo: '',
    etapa_id: '',
    tipo_campo: 'texto',
    obrigatoria: false,
    ativo: true,
    tipos_projeto_ids: [],
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [m, e, t] = await Promise.all([
        marcasEtapaAPI.list(),
        etapasAPI.list(),
        tiposProjetoAPI.list(),
      ]);
      setMarcas(m.data);
      setEtapas(e.data);
      setTiposProjeto(t.data.filter((x) => x.ativo));
    } catch {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (marca = null) => {
    if (marca) {
      setEditing(marca);
      setFormData({
        nome: marca.nome,
        campo: marca.campo,
        etapa_id: marca.etapa_id,
        tipo_campo: marca.tipo_campo || 'texto',
        obrigatoria: !!marca.obrigatoria,
        ativo: marca.ativo,
        tipos_projeto_ids: marca.tipos_projeto_ids || [],
      });
    } else {
      setEditing(null);
      setFormData({
        nome: '',
        campo: '',
        etapa_id: '',
        tipo_campo: 'texto',
        obrigatoria: false,
        ativo: true,
        tipos_projeto_ids: [],
      });
    }
    setDialogOpen(true);
  };

  const handleTipoProjetoToggle = (tipoId) => {
    setFormData((prev) => {
      const t = prev.tipos_projeto_ids || [];
      return {
        ...prev,
        tipos_projeto_ids: t.includes(tipoId) ? t.filter((x) => x !== tipoId) : [...t, tipoId],
      };
    });
  };

  const normalizeCampo = (v) =>
    (v || '')
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9_ ]/g, '')
      .replace(/\s+/g, '_');

  const handleNomeChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      nome: value,
      campo: editing ? prev.campo : normalizeCampo(value),
    }));
  };

  const handleSubmit = async () => {
    if (!formData.nome || !formData.etapa_id) {
      toast.error('Nome e etapa de origem são obrigatórios');
      return;
    }
    const payload = { ...formData, campo: normalizeCampo(formData.campo) };
    try {
      if (editing) {
        await marcasEtapaAPI.update(editing.id, payload);
        toast.success('Marca atualizada');
      } else {
        await marcasEtapaAPI.create(payload);
        toast.success('Marca criada');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar marca');
    }
  };

  const handleDelete = async (id) => {
    try {
      await marcasEtapaAPI.delete(id);
      toast.success('Marca desativada');
      loadData();
    } catch {
      toast.error('Erro ao desativar marca');
    }
  };

  const openMigrate = (marca) => {
    setMigrateMarca(marca);
    setMigrateField('numero_proposta');
    setMigrateOverwrite(false);
    setMigrateResult(null);
    setMigrateDialog(true);
  };

  const handleMigrate = async () => {
    if (!migrateMarca) return;
    try {
      setMigrating(true);
      const resp = await marcasEtapaAPI.migrateLegacy(migrateMarca.id, migrateField, migrateOverwrite);
      setMigrateResult(resp.data);
      toast.success(`${resp.data.copiados} projeto(s) migrado(s)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro na migração');
    } finally {
      setMigrating(false);
    }
  };

  const etapaNome = (id) => etapas.find((e) => e.id === id)?.nome || '—';

  if (!isAdmin()) {
    return (
      <div className="text-center py-8" data-testid="marcas-no-access">
        <p className="text-muted-foreground">Você não tem permissão para acessar esta página.</p>
      </div>
    );
  }

  // Agrupar marcas por etapa
  const marcasPorEtapa = etapas
    .map((etapa) => ({
      etapa,
      itens: marcas.filter((m) => m.etapa_id === etapa.id),
    }))
    .filter((g) => g.itens.length > 0);

  const marcasSemEtapa = marcas.filter((m) => !etapas.find((e) => e.id === m.etapa_id));

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="marcas-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Tag className="w-7 h-7 text-primary" />
            Marcas
          </h1>
          <p className="text-muted-foreground">
            Campos persistentes definidos em uma etapa que continuam visíveis (somente leitura)
            nas etapas seguintes. Ideal para protocolos, números de referência, datas marcantes.
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="new-marca-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nova Marca
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : marcas.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Tag className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>Nenhuma marca cadastrada ainda.</p>
            <p className="text-sm mt-1">Clique em "Nova Marca" para começar.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {marcasPorEtapa.map(({ etapa, itens }) => (
            <Card key={etapa.id} data-testid={`marcas-group-${etapa.id}`}>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Badge variant="outline" className="font-mono">{etapa.ordem}</Badge>
                  {etapa.nome}
                  <span className="text-xs text-muted-foreground font-normal">
                    ({itens.length} {itens.length === 1 ? 'marca' : 'marcas'})
                  </span>
                </CardTitle>
                <CardDescription className="text-xs">
                  Preenchida nesta etapa, exibida em todas as etapas seguintes.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {itens.map((m) => {
                  const Icon = TIPO_ICONS[m.tipo_campo] || Type;
                  return (
                    <div
                      key={m.id}
                      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/30 transition-colors"
                      data-testid={`marca-item-${m.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium truncate">{m.nome}</span>
                            <Badge variant="secondary" className="text-xs">
                              {TIPO_LABELS[m.tipo_campo]}
                            </Badge>
                            {m.obrigatoria && (
                              <Badge className="text-xs bg-amber-500 hover:bg-amber-600">Obrigatória</Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground font-mono">{m.campo}</p>
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openMigrate(m)}
                          title="Migrar dados legados para esta Marca"
                          data-testid={`migrate-marca-${m.id}`}
                        >
                          <Database className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleOpenDialog(m)}
                          data-testid={`edit-marca-${m.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="text-destructive"
                              data-testid={`delete-marca-${m.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remover Marca</AlertDialogTitle>
                              <AlertDialogDescription>
                                Tem certeza que deseja remover "{m.nome}"? Projetos existentes
                                manterão o valor já preenchido.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(m.id)}>
                                Remover
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ))}
          {marcasSemEtapa.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base text-muted-foreground">Sem etapa vinculada</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {marcasSemEtapa.map((m) => (
                  <div key={m.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <span>{m.nome}</span>
                    <Button size="sm" variant="outline" onClick={() => handleOpenDialog(m)}>
                      Editar
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Marca' : 'Nova Marca'}</DialogTitle>
            <DialogDescription>
              A marca é preenchida na etapa de origem e passa a ser exibida (somente leitura) em
              todas as etapas seguintes.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="marca-nome">Nome *</Label>
              <Input
                id="marca-nome"
                placeholder="Ex: Protocolo"
                value={formData.nome}
                onChange={(e) => handleNomeChange(e.target.value)}
                data-testid="marca-nome-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="marca-campo">Identificador técnico</Label>
              <Input
                id="marca-campo"
                placeholder="protocolo"
                value={formData.campo}
                onChange={(e) => setFormData({ ...formData, campo: normalizeCampo(e.target.value) })}
                data-testid="marca-campo-input"
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Chave interna (minúscula, sem espaços). Gerada automaticamente a partir do nome.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select
                  value={formData.tipo_campo}
                  onValueChange={(v) => setFormData({ ...formData, tipo_campo: v })}
                >
                  <SelectTrigger data-testid="marca-tipo-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="texto">Texto</SelectItem>
                    <SelectItem value="numero">Número</SelectItem>
                    <SelectItem value="data">Data</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Etapa de origem *</Label>
                <Select
                  value={formData.etapa_id}
                  onValueChange={(v) => setFormData({ ...formData, etapa_id: v })}
                >
                  <SelectTrigger data-testid="marca-etapa-select">
                    <SelectValue placeholder="Selecione a etapa" />
                  </SelectTrigger>
                  <SelectContent>
                    {etapas.map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.ordem}. {e.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <Label htmlFor="marca-obrigatoria" className="font-medium">Obrigatória</Label>
                <p className="text-xs text-muted-foreground">
                  Bloqueia o avanço se não estiver preenchida.
                </p>
              </div>
              <Switch
                id="marca-obrigatoria"
                checked={formData.obrigatoria}
                onCheckedChange={(v) => setFormData({ ...formData, obrigatoria: v })}
                data-testid="marca-obrigatoria-switch"
              />
            </div>

            <div className="space-y-2">
              <Label>Tipos de Projeto</Label>
              <p className="text-xs text-muted-foreground">
                Deixe vazio para aplicar a todos.
              </p>
              <div className="border rounded-md p-3 space-y-2 max-h-40 overflow-y-auto">
                {tiposProjeto.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhum tipo cadastrado</p>
                ) : (
                  tiposProjeto.map((t) => (
                    <div key={t.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`marca-tp-${t.id}`}
                        checked={(formData.tipos_projeto_ids || []).includes(t.id)}
                        onCheckedChange={() => handleTipoProjetoToggle(t.id)}
                        data-testid={`marca-tipo-projeto-${t.id}`}
                      />
                      <Label htmlFor={`marca-tp-${t.id}`} className="text-sm font-normal cursor-pointer">
                        {t.nome}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} data-testid="save-marca-btn">
              <Save className="w-4 h-4 mr-2" />
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: migrar dados legados para Marca */}
      <Dialog open={migrateDialog} onOpenChange={setMigrateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-primary" />
              Migrar dados legados → Marca
            </DialogTitle>
            <DialogDescription>
              Copia valores antigos (campos hardcoded do projeto) para a marca{' '}
              <span className="font-mono font-semibold">{migrateMarca?.nome}</span>.
              Por padrão não sobrescreve valores já existentes na marca.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Campo legado de origem</Label>
              <Select value={migrateField} onValueChange={setMigrateField}>
                <SelectTrigger data-testid="migrate-field-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEGACY_FIELDS.map((f) => (
                    <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Para "Nº da Proposta" use o campo <span className="font-mono">numero_proposta</span>.
              </p>
            </div>

            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <Label className="font-medium">Sobrescrever valores existentes</Label>
                <p className="text-xs text-muted-foreground">
                  Se desmarcado, projetos que já tenham valor na marca serão pulados.
                </p>
              </div>
              <Switch
                checked={migrateOverwrite}
                onCheckedChange={setMigrateOverwrite}
                data-testid="migrate-overwrite-switch"
              />
            </div>

            {migrateResult && (
              <div className="rounded-lg border bg-muted/40 p-3 space-y-1 text-sm">
                <p className="font-semibold text-emerald-700 dark:text-emerald-400">
                  {migrateResult.copiados} projeto(s) migrado(s)
                </p>
                {migrateResult.pulados > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {migrateResult.pulados} pulado(s) (já tinham valor na marca)
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Total analisado: {migrateResult.total_analisados}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMigrateDialog(false)}>
              Fechar
            </Button>
            <Button onClick={handleMigrate} disabled={migrating} data-testid="run-migrate-btn">
              <Database className="w-4 h-4 mr-2" />
              {migrating ? 'Migrando...' : 'Executar migração'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Marcas;
